import React, { useState, useEffect } from "react";
import { i18n } from '../../translate/i18n';
import { isEmpty } from 'lodash';
import {
  Paper, Typography, FormControl, IconButton, Container, TextField,
  Divider, Avatar, useMediaQuery, Box, FormControlLabel, Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Spinner from '../../components/Spinner/index';
import confirm from '../../UI/Confirm/confirm';
import PhotoUpload from '../../UI/PhotoUpload';
import PhotoBarUploading from '../../UI/PhotoBarUploading/PhotoBarUploading';
import DoneIcon from '@mui/icons-material/Done';
import { useDispatch, useSelector } from "react-redux";
import EFeatureFlags from "../../utils/EFeatureFlags";
import * as actions from '../../store/actions';
import { useHistory } from "react-router-dom";

type TAdminFeatureFlag = {
  name: EFeatureFlags,
  active: boolean,
}

const Settings: React.FC = (props) => {
  const matches = useMediaQuery('(min-width:1200px)');
  const dispatch = useDispatch();
  const history = useHistory();

  const [editMode, setEditMode] = useState(false);

  const [name, setName] = useState("");
  const [profile, setProfile] = useState<any>([])

  const [featureFlags, setFeatureFlags] = useState<EFeatureFlags[]>([]);
  const [adminFeatureFlags, setAdminFeatureFlags] = useState<TAdminFeatureFlag[]>([]);

  const tenantId = useSelector((state: any) => state.auth.selectedAccess.tenant._id);
  const tenant =  useSelector((state: any) => state.tenants.tenant);
  const loadingTenant = useSelector((state: any) => state.tenants.loading);

  useEffect(() => {
    if(tenantId) {
      dispatch(actions.fetchTenantsById(tenantId));
    }
  }, [tenantId])

  useEffect(() => {
    if (!isEmpty(tenant)) {
      setFeatureFlags(tenant.featureFlags || []);
      setName(tenant.name);
      const profileArray = []
      profileArray.push(tenant.photo)
      setProfile(profileArray)
    }
  }, [tenant])

  const handleBack = () => {
    history.goBack();
  }

  const handleChangeFeatureFlags = (flag: EFeatureFlags, shouldInclude: boolean) => {
    if (shouldInclude) {
      setFeatureFlags([
        ...featureFlags.filter((flagName) => flagName !== flag),
        flag
      ]);
    } else {
      setFeatureFlags([
        ...featureFlags.filter((flagName) => flagName !== flag),
      ]);
    }

    setAdminFeatureFlags([
      ...adminFeatureFlags.filter((adminFlag) => adminFlag.name !== flag),
      { name: flag, active: shouldInclude}
    ]);
  }

  const handleClose = () => {
    confirm(i18n.t('handleCloseMyCompany.confirm'),
      {
        title: i18n.t('handleCloseMyCompany.title'),
        titleColor: 'red',
        disableBackdrop: true
      })
      .then(
        () => {
          console.log('continue editing');
        },
        async () => {
          setFeatureFlags(tenant.featureFlags);
          setEditMode(false)
        },
    )
  }

  const handleSubmit = () => {
    dispatch(actions.updateFeatureFlagsAdmin(tenant._id, { featureFlags: adminFeatureFlags }));
    setEditMode(false);
  }

  return <>
    <IconButton onClick={handleBack} sx={{ position: 'fixed', margin: 10 }} size="large"><ArrowBack /></IconButton>
    <Box sx={matches ? styles.divContainer : {...styles.divContainerSmall, flexDirection: 'column'}}>
      <Container maxWidth='sm' sx={{ ...styles.container, position: 'relative', opacity: loadingTenant ? 0.3 : 1 }}>
        {loadingTenant && <Spinner overlay />}
        <Paper>
          <FormControl sx={styles.opacityContainer}>
            <Box sx={styles.formTitle}>
              <Typography variant='h6'>{i18n.t('typographyMyComp.company') as string}</Typography>
              <Box sx={styles.submitButtonsContainer}>
                {editMode ?
                  <>
                    <IconButton onClick={handleClose} size="large"><CloseIcon color='error' /></IconButton>
                    <IconButton onClick={handleSubmit} size="large" children={<DoneIcon color='success' />}/>
                  </> :
                  <>
                    <IconButton onClick={() => setEditMode(true)} size="large"><EditIcon /></IconButton>
                  </>
                }
              </Box>
            </Box>
            <Divider />
            <>
              <Box sx={{...styles.imageInput, flexDirection: 'column'}}>
                {!editMode && profile && !profile[0] ?
                  <Avatar sx={styles.avatar}>LOGO</Avatar>
                  : (<>
                    <PhotoUpload photo={profile} />
                    <PhotoBarUploading photo={profile} />
                  </>)}
              </Box>
              <TextField
                variant="standard"
                disabled
                name='name'
                value={name}
                margin='normal'
              />
              <Container sx={styles.switchesContainer}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={!editMode}
                        checked={featureFlags.includes(EFeatureFlags.BLOCK_QR_CODE_READING_WITHOUT_LOGIN)}
                        onChange={event => handleChangeFeatureFlags(EFeatureFlags.BLOCK_QR_CODE_READING_WITHOUT_LOGIN, event.target.checked)}
                        color="primary"
                      />
                    }
                    label="QR Codes de equipamentos precisam de login para serem lidos"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={!editMode}
                        checked={featureFlags.includes(EFeatureFlags.FINISHED_ACTIONS_ADMINS_NOTIFICATION)}
                        onChange={event => handleChangeFeatureFlags(EFeatureFlags.FINISHED_ACTIONS_ADMINS_NOTIFICATION, event.target.checked)}
                        color="primary"
                      />
                    }
                    label="Administradores são notificados quando ações forem finalizadas"
                  />
                </FormControl>
              </Container>
            </>
          </FormControl>
        </Paper>
      </Container>
    </Box>
  </>;
}

const styles = {
  arrowBack: {
    position: 'fixed',
    margin: 10,
  },
  divContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: 50,
  },
  divContainerSmall: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    padding: 10,
    margin: 0,
    marginLeft: 10,
  },
  avatarContainer: {
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    width: 150,
    height: 150,
    marginTop: 20,
  },
  avatarButton: {
    margin: 5,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  imageInput: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  opacityContainer: {
    // opacity: loadingEmployee ? '0.3' : '1'
    padding: '20px'
  },
  formLabels: {
    display: 'flex',
    margin: '20px 0',
    color: 'grey',
  },
  formLabelTitle: {
    color: '#000',
    marginRight: 10,
  },
  formLabelValue: {
    fontWeight: 'bold',
  },
  submitButtonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px 0',
  },
  formTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  switchesContainer: {
    margin: '1rem 0',
    paddingLeft: 0,
  }
}

export default Settings;